import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { useState, useEffect, useCallback } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { validateRut } from "@fdograph/rut-utilities";
import { propuestasApi } from "src/services/propuestasApi";
import { fechaActual } from "src/utils/formatTime";
import { useSnackbar } from "src/components/snackbar";
import { LoadingButton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  rejectButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  otpInput: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
  dialogContent: {
    minWidth: 300,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  otpInfo: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  commentInput: {
    marginTop: theme.spacing(2),
  },
  timerText: {
    color: theme.palette.warning.main,
    fontWeight: "bold",
    marginTop: theme.spacing(1),
  },
  termsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  termsLink: {
    marginLeft: theme.spacing(1),
    textDecoration: "underline",
    color: theme.palette.primary.main,
    cursor: "pointer",
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: 4,
  },
}));

const ResponseDialog = ({
  open,
  onClose,
  dialogType,
  setProposalStatus,
  token,
  refetch,
  contactClient,
  nombreCliente,
}) => {
  const classes = useStyles();
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpSentTime, setOtpSentTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [showTermsError, setShowTermsError] = useState(false);

  const [generarOTP, { isLoading: enviandoCodigo }] =
    propuestasApi.useGenerarOTPMutation();
  const [verificarOTP, { isLoading: verificandoOTP }] =
    propuestasApi.useVerificarOTPMutation();
  const [rechazarPropuesta, { isLoading: rechazandoPropuesta }] =
    propuestasApi.useRechazarPropuestaMutation();
  const { enqueueSnackbar } = useSnackbar();

  const OTP_VALIDITY_MINUTES = 5;
  const OTP_VALIDITY_MS = OTP_VALIDITY_MINUTES * 60 * 1000;

  // Schema de validación único con validaciones condicionales
  const ValidationSchema = Yup.object().shape({
    // Validaciones para flujo de aceptación - Paso 1
    nombre: Yup.string().when(["dialogType"], {
      is: (dialogType) => dialogType === "ACEPTADA",
      then: Yup.string()
        .trim()
        .required("El nombre completo es obligatorio")
        .min(2, "El nombre debe tener al menos 2 caracteres")
        .max(100, "El nombre no puede exceder los 100 caracteres")
        .matches(
          /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/,
          "El nombre solo puede contener letras y espacios",
        ),
      otherwise: Yup.string(),
    }),

    rut: Yup.string().when(["dialogType"], {
      is: (dialogType) => dialogType === "ACEPTADA",
      then: Yup.string()
        .trim()
        .required("El RUT es obligatorio")
        .test("rut-valido", "Ingrese un RUT válido", (value) => {
          if (!value) return false;
          // Limpiar el RUT de espacios y puntos/guiones extras
          const rutLimpio = value.replace(/[.\s]/g, "");
          return validateRut(rutLimpio);
        }),
      otherwise: Yup.string(),
    }),

    cargo: Yup.string().when(["dialogType"], {
      is: (dialogType) => dialogType === "ACEPTADA",
      then: Yup.string()
        .trim()
        .max(100, "El cargo no puede exceder los 100 caracteres"),
      otherwise: Yup.string(),
    }),

    // Validaciones para OTP - Paso 3
    otp: Yup.string().when(["dialogType", "currentStep"], {
      is: (dialogType, currentStep) =>
        dialogType === "ACEPTADA" && currentStep === 3,
      then: Yup.string()
        .required("El código OTP es obligatorio")
        .matches(/^\d{6}$/, "El código OTP debe tener 6 dígitos")
        .test(
          "otp-numerico",
          "El código OTP solo puede contener números",
          (value) => {
            return /^\d+$/.test(value);
          },
        ),
      otherwise: Yup.string(),
    }),

    // Validaciones para términos y condiciones - Paso 3
    acceptedTerms: Yup.boolean().when(["dialogType", "currentStep"], {
      is: (dialogType, currentStep) =>
        dialogType === "ACEPTADA" && currentStep === 3,
      then: Yup.boolean().oneOf(
        [true],
        "Debes aceptar los términos y condiciones",
      ),
      otherwise: Yup.boolean(),
    }),

    // Validaciones para rechazo
    comments: Yup.string().when(["dialogType"], {
      is: (dialogType) => dialogType === "RECHAZADA",
      then: Yup.string()
        .trim()
        .required("El motivo del rechazo es obligatorio")
        .min(10, "El motivo debe tener al menos 10 caracteres")
        .max(500, "El motivo no puede exceder los 500 caracteres"),
      otherwise: Yup.string(),
    }),

    // Campos de contexto para validaciones condicionales
    dialogType: Yup.string(),
    currentStep: Yup.number(),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      // Datos del aceptante
      nombre: "",
      rut: "",
      cargo: "",
      // OTP
      otp: "",
      acceptedTerms: false,
      // Rechazo
      comments: "",
      // Campos de contexto - valores estáticos iniciales
      dialogType: "",
      currentStep: 1,
    },
    validateOnBlur: true,
    validateOnChange: false,
    validationSchema: ValidationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      if (dialogType === "RECHAZADA") {
        await handleRejectProposal(values);
      } else if (dialogType === "ACEPTADA" && values.currentStep === 3) {
        await handleVerifyOtp(values);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
  } = formik;

  // Función para limpiar el formulario
  const clear = useCallback(() => {
    resetForm();
    setIsOtpSent(false);
    setOtpSentTime(null);
    setRemainingTime(null);
    setShowTermsError(false);
  }, [resetForm]);

  // Limpiar formulario cuando se abre el diálogo
  useEffect(() => {
    if (!open) return;

    resetForm({
      values: {
        nombre: "",
        rut: "",
        cargo: "",
        otp: "",
        acceptedTerms: false,
        comments: "",
        dialogType,
        currentStep: 1,
      },
    });

    setIsOtpSent(false);
    setOtpSentTime(null);
    setRemainingTime(null);
    setShowTermsError(false);
  }, [open, dialogType, resetForm]);

  // Timer para OTP - usando la misma lógica que el original
  useEffect(() => {
    let intervalId;

    if (otpSentTime && isOtpSent) {
      // Inicializar el contador inmediatamente
      const updateRemainingTime = () => {
        const now = new Date();
        const expirationTime = new Date(
          otpSentTime.getTime() + OTP_VALIDITY_MS,
        );
        const timeLeft = expirationTime - now;

        if (timeLeft <= 0) {
          setRemainingTime(null);
          clearInterval(intervalId);
          setFieldValue("currentStep", 2);
        } else {
          const minutes = Math.floor(timeLeft / 60000);
          const seconds = Math.floor((timeLeft % 60000) / 1000);
          setRemainingTime({ minutes, seconds });
        }
      };

      // Actualizar inmediatamente y luego cada segundo
      updateRemainingTime();
      intervalId = setInterval(updateRemainingTime, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [otpSentTime, isOtpSent, OTP_VALIDITY_MS, setFieldValue]);

  // Validación del paso 1 antes de continuar
  const handleSiguientePaso = useCallback(async () => {
    // Mostrar mensajes de error de inmediato
    formik.setTouched({
      ...formik.touched,
      nombre: true,
      rut: true,
    });

    const errors = await formik.validateForm();
    const hasErrors = !!(errors?.nombre || errors?.rut);

    if (!hasErrors && values.nombre.trim() && values.rut.trim()) {
      setFieldValue("currentStep", 2);
    }
  }, [formik, setFieldValue, values.nombre, values.rut]);

  // Generar OTP
  const handleContinuarConOTP = useCallback(async () => {
    try {
      const otpTime = fechaActual().toDate();
      setOtpSentTime(otpTime);

      await generarOTP({ token }).unwrap();

      setIsOtpSent(true);
      setFieldValue("currentStep", 3);

      enqueueSnackbar(
        "Se ha enviado un código OTP a tu correo electrónico. Revisa la carpeta de SPAM si no lo encuentras.",
      );
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          "Ocurrió un error al generar el código OTP. Reintente nuevamente.",
        { variant: "error" },
      );
    }
  }, [enqueueSnackbar, generarOTP, setFieldValue, token]);

  // Verificar OTP
  const handleVerifyOtp = useCallback(
    async (formValues) => {
      if (!formValues.acceptedTerms) {
        setShowTermsError(true);
        return;
      }

      setShowTermsError(false);

      try {
        await verificarOTP({
          token,
          otp: formValues.otp,
          nombreCliente,
          datosAceptante: {
            nombre: formValues.nombre,
            rut: formValues.rut,
            cargo: formValues.cargo,
          },
        }).unwrap();

        setRemainingTime(null);
        setOtpSentTime(null);
        setProposalStatus(
          dialogType === "ACEPTADA" ? "ACEPTADO_CLIENTE" : "RECHAZADA",
        );
        enqueueSnackbar("Código verificado. Propuesta aceptada correctamente.");
        refetch();
        onClose();
      } catch (error) {
        console.error("Error al verificar el OTP:", error);

        if (
          error?.data?.message === "OTP expirado o inválido" ||
          error?.data?.message === "OTP expirado."
        ) {
          enqueueSnackbar(
            "El código OTP ingresado ha expirado o no es válido. Por favor, solicite uno nuevo.",
            { variant: "error" },
          );
          clear();
        } else if (error?.data.message === "OTP incorrecto.") {
          setFieldValue("otp", ""); // Limpiar OTP en caso de error
          enqueueSnackbar(
            "Código OTP incorrecto. Por favor, verifica el código ingresado.",
            { variant: "error" },
          );
        } else {
          enqueueSnackbar(
            "Ocurrió un error desconocido al verificar la propuesta. Contáctese con el remitente de la propuesta.",
            { variant: "error" },
          );
        }
      }
    },
    [
      clear,
      dialogType,
      enqueueSnackbar,
      nombreCliente,
      onClose,
      refetch,
      setFieldValue,
      setProposalStatus,
      token,
      verificarOTP,
    ],
  );

  // Rechazar propuesta
  const handleRejectProposal = useCallback(
    async (formValues) => {
      try {
        await rechazarPropuesta({
          token,
          comentarios: formValues.comments,
          nombreCliente,
        }).unwrap();

        enqueueSnackbar("Propuesta rechazada correctamente.");
        setProposalStatus(dialogType);
        refetch();
        onClose();
      } catch (error) {
        console.error("handleRejectProposal", error);
        enqueueSnackbar(
          error?.data?.message || "Ocurrió un error al rechazar la propuesta",
          {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "right",
            },
          },
        );
      }
    },
    [
      dialogType,
      enqueueSnackbar,
      nombreCliente,
      onClose,
      rechazarPropuesta,
      refetch,
      setProposalStatus,
      token,
    ],
  );

  // Formatear RUT mientras el usuario escribe
  const handleRutChange = useCallback(
    (e) => {
      let value = e.target.value;

      // Remover caracteres no válidos
      value = value.replace(/[^0-9kK.-]/g, "");

      // Formatear RUT automáticamente
      if (value.length > 1) {
        // Remover puntos y guión existentes
        const rutLimpio = value.replace(/[.-]/g, "");

        if (rutLimpio.length > 1) {
          const cuerpo = rutLimpio.slice(0, -1);
          const dv = rutLimpio.slice(-1);

          // Agregar puntos cada 3 dígitos desde la derecha
          const cuerpoFormateado = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
          value = `${cuerpoFormateado}-${dv}`;
        }
      }

      setFieldValue("rut", value);
    },
    [setFieldValue],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {dialogType === "ACEPTADA" ? "Aceptar Propuesta" : "Rechazar Propuesta"}
      </DialogTitle>

      <FormikProvider value={formik}>
        <Form>
          <DialogContent className={classes.dialogContent}>
            {dialogType === "ACEPTADA" ? (
              <>
                {/* PASO 1: Datos de quien acepta */}
                {values.currentStep === 1 && (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Datos de la persona que acepta la propuesta:
                    </Typography>

                    <TextField
                      autoFocus
                      required
                      margin="dense"
                      label="Nombre completo"
                      fullWidth
                      variant="outlined"
                      name="nombre"
                      value={values.nombre}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.nombre && Boolean(errors.nombre)}
                      helperText={touched.nombre && errors.nombre}
                      style={{ marginTop: 16 }}
                    />

                    <TextField
                      required
                      margin="dense"
                      label="RUT"
                      fullWidth
                      variant="outlined"
                      name="rut"
                      value={values.rut}
                      onChange={handleRutChange}
                      onBlur={handleBlur}
                      error={touched.rut && Boolean(errors.rut)}
                      helperText={touched.rut && errors.rut}
                      placeholder="12.345.678-9"
                      inputProps={{ maxLength: 12 }}
                    />

                    <TextField
                      margin="dense"
                      label="Cargo (opcional)"
                      fullWidth
                      variant="outlined"
                      name="cargo"
                      value={values.cargo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.cargo && Boolean(errors.cargo)}
                      helperText={touched.cargo && errors.cargo}
                    />
                  </>
                )}

                {/* PASO 2: Solicitud OTP */}
                {values.currentStep === 2 && (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Para garantizar la seguridad de tu aceptación, presiona el
                      botón y te enviaremos un código OTP a tu correo registrado
                      ({contactClient}).
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginTop: 16 }}
                    >
                      <strong>Datos confirmados:</strong>
                      <br />
                      Nombre: {values.nombre}
                      <br />
                      RUT: {values.rut}
                      <br />
                      {values.cargo && `Cargo: ${values.cargo}`}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleContinuarConOTP}
                      disabled={enviandoCodigo}
                      style={{ marginTop: 16 }}
                    >
                      {enviandoCodigo ? "Enviando..." : "Solicitar Código OTP"}
                    </Button>
                  </>
                )}

                {/* PASO 3: Verificación OTP */}
                {values.currentStep === 3 && isOtpSent && (
                  <>
                    <TextField
                      className={classes.otpInput}
                      label="Código OTP"
                      variant="outlined"
                      name="otp"
                      value={values.otp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={verificandoOTP}
                      error={touched.otp && Boolean(errors.otp)}
                      helperText={touched.otp && errors.otp}
                      inputProps={{ maxLength: 6 }}
                    />

                    <div className={classes.termsContainer}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.acceptedTerms}
                            onChange={(e) =>
                              setFieldValue("acceptedTerms", e.target.checked)
                            }
                            onBlur={() =>
                              formik.setFieldTouched("acceptedTerms", true)
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Acepto los
                            <a
                              href="https://www.dropbox.com/scl/fi/e767r1vopyu6oqj68xyel/T-rminos-y-Condiciones.pdf?rlkey=fhaqvi47f0f367x624buc8ow9&st=eyiqj9qh&dl=0"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.termsLink}
                            >
                              términos y condiciones
                            </a>
                          </Typography>
                        }
                      />
                    </div>

                    {(showTermsError ||
                      (touched.acceptedTerms && errors.acceptedTerms)) && (
                      <FormHelperText className={classes.errorText}>
                        {errors.acceptedTerms ||
                          "Debes aceptar los términos y condiciones."}
                      </FormHelperText>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={formik.handleSubmit}
                      fullWidth
                      style={{ marginTop: 16 }}
                      disabled={verificandoOTP}
                    >
                      {verificandoOTP ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Verificar OTP"
                      )}
                    </Button>

                    <Typography className={classes.otpInfo}>
                      El código OTP es válido por 5 minutos.
                    </Typography>

                    {remainingTime ? (
                      <Typography className={classes.timerText}>
                        Tiempo restante: {remainingTime.minutes}:
                        {remainingTime.seconds.toString().padStart(2, "0")}{" "}
                        minutos
                      </Typography>
                    ) : (
                      <Typography color="error" variant="body2">
                        El código OTP ha expirado. Solicita uno nuevo.
                      </Typography>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* Flujo de rechazo */}
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  label="Motivo del rechazo"
                  type="text"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  name="comments"
                  value={values.comments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={classes.commentInput}
                  error={touched.comments && Boolean(errors.comments)}
                  helperText={touched.comments && errors.comments}
                  inputProps={{ maxLength: 500 }}
                />
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} color="primary">
              Cancelar
            </Button>

            {dialogType === "ACEPTADA" ? (
              <>
                {/* PASO 1: Botón para continuar con datos */}
                {values.currentStep === 1 && (
                  <Button
                    onClick={handleSiguientePaso}
                    variant="contained"
                    color="primary"
                    disabled={!values.nombre.trim() || !values.rut.trim()}
                  >
                    Continuar
                  </Button>
                )}

                {/* PASO 2: Botón volver */}
                {values.currentStep === 2 && (
                  <Button
                    onClick={() => {
                      setFieldValue("currentStep", 1);
                    }}
                    color="primary"
                  >
                    Volver
                  </Button>
                )}

                {/* PASO 3: Botón volver */}
                {values.currentStep === 3 && (
                  <Button
                    onClick={() => {
                      setFieldValue("currentStep", 2);
                      setFieldValue("otp", "");
                    }}
                    color="primary"
                    disabled={verificandoOTP}
                  >
                    Volver
                  </Button>
                )}
              </>
            ) : (
              <LoadingButton
                onClick={formik.handleSubmit}
                variant="contained"
                className={classes.rejectButton}
                disabled={!values.comments || rechazandoPropuesta}
                loading={rechazandoPropuesta}
              >
                Rechazar
              </LoadingButton>
            )}
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

export default React.memo(ResponseDialog);
